import React, {useRef, useState} from 'react';
import { Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { Field, FormikProvider, useFormik } from 'formik';
import { finalReportOfSupervisingEngineerValidationSchema } from 'app/edozvola/cases/case-technical-inspection/model/case-technical-inspection.schema';
import { finalReportOfSupervisingEngineerInitial } from 'app/edozvola/cases/case-technical-inspection/model/case-technical-inspection.model';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { Button, Input, PredictiveInput } from 'edozvola-frontend-lib/dist/ui/components';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { APP_DATE_FORMAT, MAX_FILE_SIZE, messages } from 'app/config/constants';
import { ChamberMemberCodebookModel } from 'app/shared/model/shared.model';
import {
  fetchFinalReportQuery,
  saveFinalReportQuery,
} from 'app/edozvola/cases/case-technical-inspection/service/case-technical-inspection.queries';
import { Loader } from 'app/shared/loader/loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import { openFileInNewTab } from 'app/shared/util/file-utils';
import { getFinalReportFile } from 'app/edozvola/cases/case-technical-inspection/service/case-technical-inspection.service';
import error = toast.error;
import {
  fetchChamberMembersQuery,
  fetchReportTypeQuery,
  fetchWorkTypeQuery
} from 'app/edozvola/codebooks/codebooks.queries';
import { queryClient } from 'app/app';
import {PaginatedDropdown} from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";
import FileUpload from 'app/shared/file-upload/file-upload';
import { openFileInNewWindow } from 'app/shared/util/navigation-util';

export interface IFinalReportOfSupervisingEngineerFormProps {
  idFinalReport: string | null;
  setIdFinalReport: any;
  readOnly: boolean;
}

const FinalReportOfSupervisingEngineerForm = (props: IFinalReportOfSupervisingEngineerFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();

  const { data: chamberMemberOptions, isLoading: chamberMemberOptionsLoading } = fetchChamberMembersQuery();
  const { data: reportTypeOptions, isLoading: reportTypeOptionsLoading } = fetchReportTypeQuery();
  const { data: workTypeOptions, isLoading: workTypeOptionsLoading } = fetchWorkTypeQuery();

  const {data: finalReportData, isLoading: finalReportDataLoading} = fetchFinalReportQuery(props.idFinalReport)
  const {mutate: saveFinalReport, isPending: savingFinalReport} = saveFinalReportQuery(uuid, () => closeModal())

  const hiddenFileInput = useRef(null);

  const [previewAttachmentPending, setPreviewAttachmentPending] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: finalReportData || finalReportOfSupervisingEngineerInitial,
    validationSchema: finalReportOfSupervisingEngineerValidationSchema,
    onSubmit: (values) => saveFinalReport(values),
    enableReinitialize: true,
    validateOnMount: true,
  });

  const closeModal = () => {
    queryClient.invalidateQueries({ queryKey: ['final-report'] });
    props.setIdFinalReport(null);
  };

  const onChamberMemberChanged = (selected: ChamberMemberCodebookModel[]) => {
    formik.setFieldValue('supervisingEngineer.oib', selected[0] ? selected[0].oib : '');
    formik.setFieldValue('supervisingEngineer.authorizationNumber', selected);
  };

  const selectFile = () => {
    hiddenFileInput.current.click();
  };

  const extensionAllowed = (fileName: string) => {
    return fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length) === 'pdf';
  };

  const appendFile = e => {
    const selectedFile = e.target.files[0];
    if (selectedFile.size > MAX_FILE_SIZE) {
      showToast(toast.error, messages.FILE_TOO_LARGE);
    } else if (!extensionAllowed(selectedFile.name)) {
      showToast(toast.error, messages.FILE_NOT_PDF);
    } else {
      formik.setFieldValue('multipartFile', selectedFile);
      formik.setFieldValue('fileWrapper.changed', true);
    }
  };

  const downloadFile = () => {
    if (formik.values.fileWrapper) {
      setPreviewAttachmentPending(true);
      getFinalReportFile(props.idFinalReport)
        .then(data => {
          if (data) {
            openFileInNewWindow(data.url)
          }
        })
        .catch(err => showToast(error, 'Dogodila se greška prilikom preuzimanja dokumenta.'))
        .finally(() => setPreviewAttachmentPending(false));
    }
  };

  return (
    <>
      {finalReportDataLoading && <Loader />}
      {!finalReportDataLoading && (
        <Modal scrollable isOpen={props.idFinalReport !== null} size={'xl'}>
          {props.readOnly ? (
            <ModalHeader toggle={() => closeModal()}>Pregled podataka o završnom izvješću nadzornog inženjera</ModalHeader>
          ) : (
            <ModalHeader>
              {props.idFinalReport === ''
                ? 'Unos završnog izvješća nadzornog inženjera'
                : 'Uređivanje završnog izvješća nadzornog inženjera'}
            </ModalHeader>
          )}
          <ModalBody>
            <FormikProvider value={formik}>
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <div className={'form-header'}>Završno izvješće nadzornog inženjera</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta izvješća'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu izvješća'}
                      onItemSelect={selected => formik.setFieldValue('finalReportType', selected)}
                      selected={formik.values.finalReportType}
                      labelKey={'value'}
                      options={reportTypeOptionsLoading ? [] : reportTypeOptions}
                      disabled={props.readOnly}
                      error={!props.readOnly && (formik.errors.finalReportType as string)}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Vrsta radova'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu radova'}
                      onItemSelect={selected => formik.setFieldValue('workType', selected)}
                      selected={formik.values.workType}
                      labelKey={'value'}
                      options={workTypeOptionsLoading ? [] : workTypeOptions}
                      disabled={props.readOnly}
                      clearButton
                      error={!props.readOnly && (formik.errors.workType as string)}
                      showErrorMessage
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Oznaka'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.mark}
                      error={!props.readOnly && formik.errors.mark ? `${formik.errors.mark}` : ''}
                      maxLength={100}
                      onChange={event => formik.setFieldValue('mark', event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Datum izrade'} />
                    <Field
                      id="reportDate"
                      name="reportDate"
                      render={({ field }) => (
                        <FormikDatepicker
                          field={field}
                          disabled={props.readOnly}
                          setFieldValue={formik.setFieldValue}
                          dateFormat={APP_DATE_FORMAT}
                          error={!props.readOnly && formik.errors.reportDate ? `${formik.errors.reportDate}` : ''}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Dokument'} />
                    {props.readOnly ? (
                      <Input
                        disabled={props.readOnly}
                        onClick={selectFile}
                        value={formik.values.multipartFile ? formik.values.multipartFile.name : formik.values.fileWrapper?.fileName}
                      />
                    ) : (
                      <FileUpload
                        disabled={props.readOnly}
                        setFile={selectedFile => {
                          formik.setFieldValue('multipartFile', selectedFile);
                          formik.setFieldValue('fileWrapper.changed', true);
                        }}
                        value={formik.values.multipartFile ? formik.values.multipartFile.name : formik.values.fileWrapper?.fileName ?? ''}
                        appendIcon={formik.values.fileWrapper?.fileName ? 'download' : 'paperclip'}
                        appendOnClick={formik.values.fileWrapper?.fileName ? downloadFile : undefined}
                        clearButton
                        onClear={() => {
                          formik.setFieldValue('multipartFile', null);
                          formik.setFieldValue('fileWrapper.changed', true);
                          formik.setFieldValue('fileWrapper.fileName', '');
                        }}
                        previewAttachmentPending={previewAttachmentPending}
                      />
                    )}
                  </Col>
                  <input type="file" ref={hiddenFileInput} onChange={appendFile} className="hidden-upload" />
                </Row>
                <hr />
                <div className={'form-header'}>Nadzorni inženjer</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Broj ovlaštenja/Ime i prezime'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi broj ovlaštenja/ime i prezime'}
                      onItemSelect={onChamberMemberChanged}
                      selected={formik.values.supervisingEngineer.authorizationNumber}
                      labelKey={'value'}
                      options={chamberMemberOptionsLoading ? [] : chamberMemberOptions}
                      disabled={props.readOnly}
                      error={
                        !props.readOnly && formik.errors.supervisingEngineer && formik.errors.supervisingEngineer['authorizationNumber']
                          ? (formik.errors.supervisingEngineer['authorizationNumber'] as string)
                          : ''
                      }
                      showErrorMessage
                      positionFixed
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'OIB'} />
                    <Input disabled value={formik.values.supervisingEngineer.oib} />
                  </Col>
                </Row>
              </div>
            </FormikProvider>
          </ModalBody>
          <ModalFooter>
            {!props.readOnly && (
              <>
                <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
                <Button
                  label={'Spremi'}
                  size={'lg'}
                  disabled={!formik.dirty || !formik.isValid}
                  isLoading={savingFinalReport}
                  color={'info'}
                  onClick={() => formik.handleSubmit()}
                />
              </>
            )}
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}

export default FinalReportOfSupervisingEngineerForm
